/* eslint-disable import/no-unresolved */
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { getSubAccounts } from 'actions/user';
import Tooltip from 'components/onClickTooltip';
import Render from 'components/render';
import { MARGIN_TYPES } from 'constants/constants';
import { device } from 'helpers/screenSizes';
import { noop } from 'helpers/utils';
import subscribe from 'HOC/subscribe';
import { orderMarginSelector, selectedProductSelector, userSelector } from 'selectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { MobileModalCloseIconNew } from 'styles/assets/icons';
import { Product } from 'types/IProducts';
import { AccountMargin } from 'types/leverage';
import { Button } from 'UIKit';

import styles from './leverage.module.scss';
import LeverageSlider from './leverageSlider';
import { MarginModeInfo, WarningMessage } from './marginTypes/helpers';

export const getMarginModeTooltipTitle = (marginType: string) => {
  return <span className={styles.highlight} data-palette="getMarginModeTooltipTitle">{marginType}</span>;
};

export const getMarginModeTooltipBody = (marginType: string) => {
  return (
    (<div className="" data-palette="getMarginModeTooltipBody">
      <MarginModeInfo margin_mode={marginType} showTitle />
    </div>)
  );
};

const Leverage = ({ onCancel, side }: AccountMargin) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['trading', 'portfolioMargin']);
  const isTabOrMobile = useMediaQuery({ query: device.down.md });

  const user = useAppSelector(userSelector);
  const selectedProduct: Product = useAppSelector(
    state => selectedProductSelector(state)!
  );
  const orderMargins = useAppSelector(state => orderMarginSelector(state));
  const leverage = orderMargins[selectedProduct.id as number]?.leverage;

  const mainUser = user.main_account || user;
  const subAccounts = [mainUser, ...user.sub_accounts];
  const marginType = user.margin_mode || MARGIN_TYPES.ISOLATED;

  useEffect(() => {
    // subAccounts are being fetched from the profile menu; this won't be populated in the mobile.
    if (isTabOrMobile && subAccounts.length <= 1) {
      dispatch(getSubAccounts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isTabOrMobile]);

  return (
    (<div className={styles.container} data-palette="Leverage">
      <div className={styles.head}>
        <div>{t('trading:leverageSection.select_leverage')}</div>
        <div
          data-testid="close-leverage-popup"
          role="button"
          tabIndex={0}
          onKeyDown={noop}
          onClick={onCancel}
          className={styles['close-icon']}
        >
          <MobileModalCloseIconNew />
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.line}>
          <div>{t('trading:leverageSection.marginMode')}</div>
          <div className={`${styles.highlight} ${styles.capitalise}`}>
            <Tooltip
              title={getMarginModeTooltipTitle(marginType)}
              body={getMarginModeTooltipBody(marginType)}
            />
          </div>
        </div>
        <Render when={marginType === MARGIN_TYPES.CROSS}>
          <>
            <div className={styles.line} data-palette="Leverage">
              <div>
                <Tooltip
                  title={t('portfolioMargin:contract_leverage')}
                  body={t('portfolioMargin:contract_leverage_tooltip')}
                />
              </div>
              <div className={styles.highlight}>
                {leverage}
                <span>x</span>
              </div>
            </div>
            <WarningMessage
              text={t('portfolioMargin:cross_leverage_warning')}
              title={t('portfolioMargin:important')}
            />
            <div className={styles.sliderFooter} data-palette="Leverage">
              <Button data-testid="closeLeverageSectionCross" onClick={onCancel}>
                {' '}
                {t('trading:leverageSection.ok')}{' '}
              </Button>
            </div>
          </>
        </Render>
        <Render when={marginType === MARGIN_TYPES.ISOLATED}>
          <LeverageSlider side={side} onCancel={onCancel} product={selectedProduct} />
        </Render>
      </div>
    </div>)
  );
};

export default subscribe(Leverage);
